/**
 * Downscale an image file to a JPEG data URL no larger than `maxEdge` pixels on
 * its longest side. Phone photos routinely weigh 4–8 MB; the portfolio endpoint
 * receives images inline as base64, so they are shrunk before they leave the
 * browser. Files the browser cannot decode fall back to the untouched data URL.
 */
export const readImageAsResizedDataUrl = (
	file: File,
	maxEdge = 1280,
	quality = 0.85
): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
		reader.onload = () => {
			const original = reader.result as string;
			const img = new Image();
			img.onerror = () => resolve(original);
			img.onload = () => {
				const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
				if (scale === 1 && file.type === 'image/jpeg') {
					resolve(original);
					return;
				}
				const canvas = document.createElement('canvas');
				canvas.width = Math.round(img.width * scale);
				canvas.height = Math.round(img.height * scale);
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					resolve(original);
					return;
				}
				ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
				resolve(canvas.toDataURL('image/jpeg', quality));
			};
			img.src = original;
		};
		reader.readAsDataURL(file);
	});
